import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'tw:h-5 tw:gap-1 tw:rounded-4xl tw:border tw:border-transparent tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:transition-all tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&>svg]:size-3! tw:inline-flex tw:items-center tw:justify-center tw:w-fit tw:whitespace-nowrap tw:shrink-0 tw:[&>svg]:pointer-events-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive tw:overflow-hidden tw:group/badge',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:[a]:hover:bg-primary/80',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:[a]:hover:bg-secondary/80',
        destructive:
          'tw:bg-destructive/10 tw:[a]:hover:bg-destructive/20 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40 tw:text-destructive tw:dark:bg-destructive/20',
        success:
          'tw:bg-success/10 tw:text-success tw:focus-visible:ring-success/20 tw:dark:bg-success/20 tw:dark:focus-visible:ring-success/40 tw:[a]:hover:bg-success/20',
        warning:
          'tw:bg-warning/10 tw:text-warning tw:focus-visible:ring-warning/20 tw:dark:bg-warning/20 tw:dark:focus-visible:ring-warning/40 tw:[a]:hover:bg-warning/20',
        info: 'tw:bg-info/10 tw:text-info tw:focus-visible:ring-info/20 tw:dark:bg-info/20 tw:dark:focus-visible:ring-info/40 tw:[a]:hover:bg-info/20',
        subtle:
          'tw:bg-subtle/10 tw:text-subtle tw:focus-visible:ring-subtle/20 tw:dark:bg-subtle/20 tw:dark:focus-visible:ring-subtle/40 tw:[a]:hover:bg-subtle/20',
        outline:
          'tw:border-border tw:text-foreground tw:[a]:hover:bg-muted tw:[a]:hover:text-muted-foreground',
        ghost:
          'tw:hover:bg-muted tw:hover:text-muted-foreground tw:dark:hover:bg-muted/50',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
